import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import Popup from '../components/Popup';

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

  .auth-page {
    font-family: 'Inter', system-ui, sans-serif;
    min-height: 100vh;
    background: #0a0f1e;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    position: relative;
    overflow: hidden;
  }

  .auth-blob {
    position: fixed;
    border-radius: 50%;
    filter: blur(80px);
    pointer-events: none;
    z-index: 0;
  }
  .auth-blob-1 {
    width: 500px; height: 500px;
    background: rgba(99,102,241,0.12);
    top: -150px; left: -150px;
  }
  .auth-blob-2 {
    width: 400px; height: 400px;
    background: rgba(79,70,229,0.09);
    bottom: -100px; right: -100px;
  }

  .auth-card {
    position: relative;
    z-index: 1;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(99,102,241,0.2);
    border-radius: 24px;
    padding: 48px 40px;
    width: 100%;
    max-width: 500px;
    box-shadow: 0 24px 60px rgba(0,0,0,0.4);
    backdrop-filter: blur(20px);
    animation: authFadeUp 0.5s ease-out both;
  }

  @keyframes authFadeUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .auth-logo {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    font-weight: 900;
    letter-spacing: 0.08em;
    color: white;
    background: linear-gradient(135deg, #6366f1, #4f46e5);
    border-radius: 12px;
    width: 48px; height: 48px;
    margin-bottom: 20px;
    box-shadow: 0 0 20px rgba(99,102,241,0.4);
  }

  .auth-title {
    font-size: 1.75rem;
    font-weight: 900;
    color: #f1f5f9;
    margin: 0 0 6px;
    letter-spacing: -0.03em;
  }

  .auth-subtitle {
    font-size: 0.9rem;
    color: #64748b;
    margin: 0 0 28px;
  }
    
  /* Logo image */
  .auth-logo-img {
    height: 48px;
    width: auto;
    border-radius: 12px;
    margin-bottom: 20px;
    filter: drop-shadow(0 0 20px rgba(99,102,241,0.5));
  }

  /* Free features teaser */
  .auth-perks {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 28px;
  }

  .auth-perk {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11.5px;
    font-weight: 600;
    color: #818cf8;
    background: rgba(99,102,241,0.08);
    border: 1px solid rgba(99,102,241,0.18);
    border-radius: 100px;
    padding: 4px 10px;
  }

  /* Alerts */
  .auth-alert {
    border-radius: 10px;
    padding: 12px 14px;
    font-size: 0.875rem;
    margin-bottom: 20px;
    line-height: 1.5;
  }
  .auth-alert-error {
    background: rgba(239,68,68,0.1);
    border: 1px solid rgba(239,68,68,0.25);
    color: #fca5a5;
  }
  .auth-alert-success {
    background: rgba(34,197,94,0.1);
    border: 1px solid rgba(34,197,94,0.2);
    color: #86efac;
  }

  /* Form */
  .auth-form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 18px;
  }

  .auth-label {
    font-size: 13px;
    font-weight: 600;
    color: #94a3b8;
  }

  .auth-input {
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(99,102,241,0.2);
    border-radius: 10px;
    padding: 13px 16px;
    font-size: 15px;
    color: #e2e8f0;
    font-family: inherit;
    outline: none;
    transition: border-color 0.2s, background 0.2s;
    width: 100%;
    box-sizing: border-box;
  }
  .auth-input::placeholder { color: #334155; }
  .auth-input:focus {
    border-color: rgba(99,102,241,0.55);
    background: rgba(99,102,241,0.06);
  }

  .auth-submit {
    width: 100%;
    padding: 14px;
    background: linear-gradient(135deg, #6366f1, #4f46e5);
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 0.95rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 4px 20px rgba(99,102,241,0.35);
    font-family: inherit;
    margin-top: 8px;
    margin-bottom: 24px;
  }
  .auth-submit:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(99,102,241,0.55);
  }
  .auth-submit:active { transform: translateY(0); }

  .auth-links {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }

  .auth-divider {
    width: 100%;
    height: 1px;
    background: rgba(99,102,241,0.1);
    margin: 4px 0;
  }

  .auth-link {
    color: #818cf8;
    text-decoration: none;
    font-size: 14px;
    font-weight: 600;
    transition: color 0.2s;
  }
  .auth-link:hover { color: #a5b4fc; }

  .auth-footer-text {
    font-size: 14px;
    color: #475569;
  }

  .auth-tos {
    font-size: 12px;
    color: #334155;
    text-align: center;
    line-height: 1.6;
    margin-top: 16px;
  }

  .auth-tos a {
    color: #6366f1;
    text-decoration: none;
  }
  .auth-tos a:hover { text-decoration: underline; }

  /* Google OAuth button */
  .auth-google-btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 13px 16px;
    background: #fff;
    color: #3c4043;
    border: 1px solid #dadce0;
    border-radius: 12px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    text-decoration: none;
    transition: box-shadow 0.2s, background 0.2s;
    box-sizing: border-box;
    margin-bottom: 16px;
  }
  .auth-google-btn:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.18); background: #f8f9fa; }

  .auth-or-divider {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
    color: #334155;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .auth-or-divider::before, .auth-or-divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(99,102,241,0.12);
  }

  @media (max-width: 480px) {
    .auth-card { padding: 36px 24px; }
  }
`;

const PERKS = ['20 free links', 'Click analytics', 'QR codes', 'No card needed'];

const Signup = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  // Popup state
  const [popup, setPopup] = useState({ isOpen: false, title: '', message: '', type: 'confirm', onConfirm: null });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/signup`, { username, email, password });
      setSuccess(response.data.message || 'Account created! Check your email to verify.');
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Signup failed';
      if (errorMessage.includes('already exists')) {
        setPopup({
          isOpen: true,
          title: 'Account Exists',
          message: 'User already exists. Redirecting to login page...',
          type: 'info',
          onConfirm: () => navigate('/login')
        });
      } else {
        setError(errorMessage);
      }
    }
  };

  return (
    <div className="auth-page">
      <style>{STYLES}</style>

      <div className="auth-blob auth-blob-1" />
      <div className="auth-blob auth-blob-2" />

      <div className="auth-card">
        {/* Logo */}
        <img src={new URL('../assets/images/logo.png', import.meta.url).href} alt="RDRTO Logo" className="auth-logo-img" />

        <h1 className="auth-title">Create your account</h1>
        <p className="auth-subtitle">Start shortening smarter — it's free forever.</p>

        {/* Free perks */}
        <div className="auth-perks">
          {PERKS.map(p => (
            <span className="auth-perk" key={p}>✓ {p}</span>
          ))}
        </div>

        {/* Alerts */}
        {success && (
          <div className="auth-alert auth-alert-success">
            ✅ {success}
            <br />
            <Link to="/login" className="auth-link" style={{ fontSize: 13, marginTop: 6, display: 'inline-block' }}>
              Go to Login →
            </Link>
          </div>
        )}

        {error && (
          <div className="auth-alert auth-alert-error">{error}</div>
        )}

        {!success && (
          <form onSubmit={handleSubmit}>
            <div className="auth-form-group">
              <label className="auth-label" htmlFor="su-username">Username</label>
              <input
                id="su-username"
                className="auth-input"
                type="text"
                placeholder="yourname"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>

            <div className="auth-form-group">
              <label className="auth-label" htmlFor="su-email">Email address</label>
              <input
                id="su-email"
                className="auth-input"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="auth-form-group">
              <label className="auth-label" htmlFor="su-password">Password</label>
              <input
                id="su-password"
                className="auth-input"
                type="password"
                placeholder="Min. 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <button type="submit" className="auth-submit">Create Free Account →</button>
          </form>
        )}

        <div className="auth-links">
          {/* Google Sign In */}
          <div className="auth-or-divider">or</div>
          <a
            href={`${API_BASE_URL}/api/auth/google`}
            className="auth-google-btn"
          >
            <svg width="20" height="20" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
            </svg>
            Continue with Google
          </a>
          <div className="auth-divider" />
          <p className="auth-footer-text">
            Already have an account?{' '}
            <Link to="/login" className="auth-link">Sign in</Link>
          </p>
        </div>

        <p className="auth-tos">
          By signing up you agree to our{' '}
          <Link to="/terms">Terms of Service</Link> and{' '}
          <Link to="/privacy">Privacy Policy</Link>.
        </p>
      </div>

      <Popup
        isOpen={popup.isOpen}
        title={popup.title}
        message={popup.message}
        type={popup.type}
        onConfirm={popup.onConfirm || (() => setPopup(p => ({ ...p, isOpen: false })))}
        onCancel={() => setPopup(p => ({ ...p, isOpen: false }))}
      />
    </div>
  );
};

export default Signup;
